import React, { useState, useEffect, useCallback, useMemo, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Plus, Minus, MessageCircle, Check } from 'lucide-react';

const PremiumMenuModal = ({ isOpen, onClose }) => {
  const [selectedItems, setSelectedItems] = useState({
    meats: [],
    potjies: [],
    sides: [],
    extras: [],
    braaiMaster: false
  });
  const [otherExtras, setOtherExtras] = useState('');

  // Menu data with prices - memoized to prevent recreation
  const menuData = useMemo(() => ({
    meats: [
      { id: '2-meat-roll', name: '2 Meat Roll', price: 1100, priceRange: [70, 1100], image: '/meat-roll.jpg' },
      { id: '3-meat-roll', name: '3 Meat Roll', price: 1650, priceRange: [100, 1650], image: '/meat-roll.jpg' },
      { id: 'lamb-roll', name: 'Lamb Roll', price: 660, image: '/lamb-roll.jpg' },
      { id: 'lamb-braai', name: 'Lamb Chop', price: 85, image: '/lamb-chop.jpg' },
      { id: 'pork-braai', name: 'Pork Chops', price: 85, image: '/pork-chop.jpg' },
      { id: 'sirloin-braai', name: 'Sirloin Steak', price: 75, image: '/sirloin-steak.jpg' },
      { id: 'boerewors', name: 'Boerewors', price: 55, image: '/boerewors.jpg' },
      { id: 'whole-lamb', name: 'Whole Lamb', price: 2500, image: '/whole-lamb-spit.jpg' },
      { id: 'whole-lamb-2', name: 'Whole Lamb 2', price: 5000, image: '/spit-braai-image.jpg' }
    ],
    potjies: [
      { id: 'chicken-potjie', name: 'Chicken Potjie', price: 550, image: '/chicken-potjie.jpg' },
      { id: 'pork-potjie', name: 'Pork Potjie', price: 750, image: '/pork-potjie.jpg' },
      { id: 'lamb-potjie', name: 'Lamb Potjie', price: 950, image: '/lamb-potjie.jpg' },
      { id: 'oxtail-potjie', name: 'Oxtail Potjie', price: 1150, image: '/oxtail-potjie.jpg' }
    ],
    sides: [
      { id: 'greek-salad', name: 'Greek Salad', price: 60, image: '/greek-salad.jpg' },
      { id: 'potato-salad', name: 'Potato Salad', price: 65, image: '/potato-salad.jpg' },
      { id: 'beetroot-salad', name: 'Beetroot', price: 30, image: '/beetroot-salad.jpg' },
      { id: 'coleslaw', name: 'Coleslaw', price: 30, image: '/coleslaw.jpg' },
      { id: 'chakalaka', name: 'Chakalaka', price: 30, image: '/chakalaka.jpg' },
      { id: 'pap-gravy', name: 'Pap and Gravy', price: 30, image: '/pap-gravy.jpg' },
      { id: 'creamy-spinach', name: 'Creamy Spinach', price: 30, image: '/creamy-spinach.jpg' },
      { id: 'garlic-bread', name: 'Garlic Bread', price: 35, image: '/garlic-bread.jpg' },
      { id: 'roasted-veggies', name: 'Roasted Veggies', price: 40, image: '/roasted-veggies.jpg' }
    ],
    extras: [
      { id: 'sauces-condiments', name: 'Sauces & Condiments', price: 100, priceRange: [100, 500], image: '/sauces-condiments.jpg' },
      { id: 'round-bread-rolls', name: 'Round Bread Rolls', price: 45, image: '/round-bread-roll.jpg' },
      { id: 'garlic-loaves', name: 'Garlic Loaves', price: 60, image: '/garlic-loaves.jpg' },
      { id: 'french-loaves', name: 'French Loaves', price: 35, image: '/french-loaves.jpg' }
    ]
  }), []);


  // Calculate totals inline to avoid initialization issues
  const totals = useMemo(() => {
    // Base price for Premium Spit Braai package
    const BASE_PRICE = 6500;
    let subtotal = BASE_PRICE;
    
    // Calculate from selected items (add on top of base price)
    Object.keys(selectedItems).forEach(category => {
      if (category === 'braaiMaster') return;
      selectedItems[category].forEach(item => {
        subtotal += item.price * item.quantity;
      });
    });
    
    // Add braai master
    if (selectedItems.braaiMaster) {
      subtotal += 950;
    }
    
    const vat = subtotal * 0.15;
    const total = subtotal + vat;
    
    return { subtotal, vat, total };
  }, [selectedItems]);

  // Load saved cart from localStorage
  useEffect(() => {
    try {
      const savedCart = localStorage.getItem('ubuntu_events_management_cart');
      if (savedCart) {
        const cartData = JSON.parse(savedCart);
        if (cartData.package === 'Premium Spit Braai' && cartData.menu) {
          setSelectedItems(cartData.menu);
          setOtherExtras(cartData.otherExtras || '');
        }
      }
    } catch (error) {
      console.warn('Failed to load cart from localStorage:', error);
    }
  }, []);

  // Save cart to localStorage whenever selections change
  useEffect(() => {
    try {
      const cartData = {
        package: 'Premium Spit Braai',
        menu: selectedItems,
        otherExtras: otherExtras,
        totals: totals
      };
      localStorage.setItem('ubuntu_events_management_cart', JSON.stringify(cartData));
    } catch (error) {
      console.warn('Failed to save cart to localStorage:', error);
    }
  }, [selectedItems, otherExtras, totals]);

  const toggleItem = useCallback((category, item) => {
    setSelectedItems(prev => ({
      ...prev,
      [category]: prev[category].some(selected => selected.id === item.id)
        ? prev[category].filter(selected => selected.id !== item.id)
        : [...prev[category], { ...item, quantity: 1 }]
    }));
  }, []);

  const updateQuantity = useCallback((category, itemId, newQuantity) => {
    setSelectedItems(prev => {
      const currentCategory = prev[category];
      if (newQuantity <= 0) {
        return {
          ...prev,
          [category]: currentCategory.filter(item => item.id !== itemId)
        };
      } else {
        return {
          ...prev,
          [category]: currentCategory.map(item =>
            item.id === itemId ? { ...item, quantity: newQuantity } : item
          )
        };
      }
    });
  }, []);

  const toggleBraaiMaster = useCallback(() => {
    setSelectedItems(prev => ({
      ...prev,
      braaiMaster: !prev.braaiMaster
    }));
  }, []);


  const sendWhatsAppMessage = () => {
    // Check if any items are selected
    const hasAnyItems = selectedItems.meats.length > 0 || 
                       selectedItems.potjies.length > 0 || 
                       selectedItems.sides.length > 0 || 
                       selectedItems.extras.length > 0 || 
                       selectedItems.braaiMaster || 
                       otherExtras.trim();
    
    if (!hasAnyItems) {
      alert('Please select at least one item before sending your order.');
      return;
    }

    // Build menu sections only if they have items
    const menuSections = [];
    
    if (selectedItems.meats.length > 0) {
      menuSections.push(`🥩 Meats: ${selectedItems.meats.map(item => `${item.name} (${item.quantity}x)`).join(', ')}`);
    }
    
    if (selectedItems.potjies.length > 0) {
      menuSections.push(`🍲 Potjies: ${selectedItems.potjies.map(item => `${item.name} (${item.quantity}x)`).join(', ')}`);
    }
    
    if (selectedItems.sides.length > 0) {
      menuSections.push(`🥗 Sides: ${selectedItems.sides.map(item => `${item.name} (${item.quantity}x)`).join(', ')}`);
    }
    
    if (selectedItems.extras.length > 0) {
      menuSections.push(`🍞 Extras: ${selectedItems.extras.map(item => `${item.name} (${item.quantity}x)`).join(', ')}`);
    }
    
    if (otherExtras.trim()) {
      menuSections.push(`🍞 Other Extras: ${otherExtras}`);
    }
    
    const message = `Hi UBUNTU EVENTS MANAGEMENT,
I've chosen the Premium Spit Braai package. My menu:

${menuSections.join('\n')}
🔥 Braai Master: ${selectedItems.braaiMaster ? 'Yes' : 'No'}

Estimated Total: R${totals.total.toFixed(2)}
Please confirm availability and pricing.`;

    try {
      const encodedMessage = encodeURIComponent(message);
      const whatsappUrl = `https://wa.me/27663286231?text=${encodedMessage}`;
      window.open(whatsappUrl, '_blank');
    } catch (error) {
      console.error('Failed to open WhatsApp:', error);
      alert('Failed to open WhatsApp. Please try again or contact us directly.');
    }
  };

  const handleConfirm = () => {
    // Cart is already saved to localStorage via useEffect
    onClose();
  };

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      onClose();
    }
  }, [onClose]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, handleKeyDown]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  // Memoized MenuItem component to prevent unnecessary re-renders
  const MenuItem = memo(({ item, category, isSelected, selectedItem, onToggle, onUpdateQuantity }) => {
    const handleDecrease = useCallback((e) => {
      e.stopPropagation();
      if (selectedItem && selectedItem.quantity) {
        onUpdateQuantity(category, item.id, selectedItem.quantity - 1);
      }
    }, [category, item.id, selectedItem, onUpdateQuantity]);

    const handleIncrease = useCallback((e) => {
      e.stopPropagation();
      if (selectedItem && selectedItem.quantity) {
        onUpdateQuantity(category, item.id, selectedItem.quantity + 1);
      }
    }, [category, item.id, selectedItem, onUpdateQuantity]);

    const handleToggle = useCallback(() => {
      onToggle(category, item);
    }, [category, item, onToggle]);

    return (
      <div
        className={`card cursor-pointer transition-all duration-150 ${
          isSelected ? 'ring-2 ring-gold bg-gold/5' : 'hover:shadow-lg'
        }`}
        onClick={handleToggle}
      >
        <div className="flex items-center space-x-3">
          <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 bg-gray-200 flex items-center justify-center">
            <img
              src={item.image}
              alt={item.name}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
            <div className="w-full h-full bg-gold/20 flex items-center justify-center text-gold text-xs font-semibold" style={{display: 'none'}}>
              {item.name.charAt(0)}
            </div>
          </div>
          
          <div className="flex-1 min-w-0">
            <h4 className="font-semibold text-gray-800 truncate">{item.name}</h4>
            <p className="text-sm text-gold font-semibold">
              {item.priceRange ? `R${item.priceRange[0]}-${item.priceRange[1]}` : `R${item.price}`}
            </p>
          </div>
          
          <div className="flex items-center space-x-2">
            {isSelected && selectedItem && (
              <>
                <button
                  onClick={handleDecrease}
                  className="w-6 h-6 rounded-full bg-gold text-white flex items-center justify-center hover:bg-gold/80 transition-colors"
                >
                  <Minus size={14} />
                </button>
                <span className="w-8 text-center font-semibold">{selectedItem.quantity || 1}</span>
                <button
                  onClick={handleIncrease}
                  className="w-6 h-6 rounded-full bg-gold text-white flex items-center justify-center hover:bg-gold/80 transition-colors"
                >
                  <Plus size={14} />
                </button>
              </>
            )}
            
            {isSelected && (
              <Check size={20} className="text-gold" />
            )}
          </div>
        </div>
      </div>
    );
  });

  MenuItem.displayName = 'MenuItem';

  const MenuSection = memo(({ title, icon, category, items }) => (
    <div className="mb-8">
      <div className="flex items-center mb-6">
        <span className="text-3xl mr-3">{icon}</span>
        <h3 className="text-2xl font-playfair font-bold text-gray-800">{title}</h3>
        <div className="flex-1 h-px bg-gradient-to-r from-gold to-transparent ml-4"></div>
      </div>
      
      <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((item) => {
          const isSelected = selectedItems[category].some(selected => selected.id === item.id);
          const selectedItem = selectedItems[category].find(selected => selected.id === item.id);
          
          return (
            <MenuItem
              key={item.id}
              item={item}
              category={category}
              isSelected={isSelected}
              selectedItem={selectedItem}
              onToggle={toggleItem}
              onUpdateQuantity={updateQuantity}
            />
          );
        })}
      </div>
    </div>
  ));

  MenuSection.displayName = 'MenuSection';

  return (
    <AnimatePresence>
      {isOpen && (
          <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ duration: 0.3 }}
            className="bg-cream rounded-t-2xl sm:rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-white p-6 border-b border-gray-200">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-3xl font-playfair font-bold text-gray-800 mb-2">
                    Premium Spit Braai — Menu Selector
                  </h2>
                  <p className="text-gray-600">
                    Choose your meats, potjies, sides, and extras. Includes groceries, setup, and optional braai master.
                  </p>
                </div>
                <button
                  onClick={onClose}
                  className="text-gray-500 hover:text-gray-700 transition-colors"
                >
                  <X size={24} />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto flex-1 min-h-0">
              <MenuSection
                title="MEATS"
                icon="🥩"
                category="meats"
                items={menuData.meats}
              />

              <MenuSection
                title="POTJIES"
                icon="🍲"
                category="potjies"
                items={menuData.potjies}
              />

              <MenuSection
                title="SIDES"
                icon="🥗"
                category="sides"
                items={menuData.sides}
              />

              <MenuSection
                title="EXTRAS"
                icon="🍞"
                category="extras"
                items={menuData.extras}
              />

              {/* Other Extras Input */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="mb-8"
              >
                <div className="flex items-center mb-4">
                  <span className="text-3xl mr-3">🍞</span>
                  <h3 className="text-2xl font-playfair font-bold text-gray-800">Other Extras</h3>
                  <div className="flex-1 h-px bg-gradient-to-r from-gold to-transparent ml-4"></div>
                </div>
                
                <div className="card">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Specify any other extras you'd like
                  </label>
                  <textarea
                    value={otherExtras}
                    onChange={(e) => setOtherExtras(e.target.value)}
                    placeholder="e.g., Special sauces, additional bread, etc."
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent resize-none"
                    rows={3}
                  />
                </div>
              </motion.div>

              {/* Braai Master */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="mb-8"
              >
                <div className="flex items-center mb-4">
                  <span className="text-3xl mr-3">🔥</span>
                  <h3 className="text-2xl font-playfair font-bold text-gray-800">BRAAI MASTER</h3>
                  <div className="flex-1 h-px bg-gradient-to-r from-gold to-transparent ml-4"></div>
                </div>
                
                <div className="card">
                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedItems.braaiMaster}
                      onChange={toggleBraaiMaster}
                      className="w-5 h-5 text-gold focus:ring-gold border-gray-300 rounded"
                    />
                    <div>
                      <span className="font-semibold text-gray-800">
                        Include Professional Braai Master
                      </span>
                      <p className="text-sm text-gray-600">R950 - Our expert will handle the entire braai process</p>
                    </div>
                    {selectedItems.braaiMaster && (
                      <Check size={20} className="text-gold ml-auto" />
                    )}
                  </label>
                </div>
              </motion.div>
            </div>

            {/* Footer with totals and actions */}
            <div className="bg-white p-6 border-t border-gray-200 shrink-0">
              <div className="flex flex-col lg:flex-row justify-between items-center space-y-4 lg:space-y-0">
                {/* Totals */}
                <div className="text-center lg:text-left">
                  <div className="text-xs text-gray-500 mb-1">Base Package: R6,500.00</div>
                  <div className="text-sm text-gray-600 mb-1">Subtotal: R{totals.subtotal.toFixed(2)}</div>
                  <div className="text-sm text-gray-600 mb-1">VAT (15%): R{totals.vat.toFixed(2)}</div>
                  <div className="text-2xl font-bold text-gold">Total: R{totals.total.toFixed(2)}</div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
                  <button
                    onClick={onClose}
                    className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  
                  <button
                    onClick={handleConfirm}
                    className="px-6 py-3 bg-gold text-white rounded-lg hover:bg-gold/90 transition-colors font-semibold"
                  >
                    Confirm Menu
                  </button>
                  
                  <button
                    onClick={sendWhatsAppMessage}
                    className="px-6 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors font-semibold flex items-center space-x-2"
                  >
                    <MessageCircle size={20} />
                    <span>Send via WhatsApp</span>
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PremiumMenuModal;
